// Auth + admin-permission gate. Every rule in pb_migrations/0001_schema.js
// that says "admin only" checks `@request.auth.collectionName = "admins"`
// — since this console authenticates against that separate `admins`
// PocketBase collection (never `users`), simply being signed in here *is*
// the admin claim. No custom-claims round-trip needed the way Firebase's
// `getIdTokenResult()` was.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ClientResponseError, type RecordModel } from "pocketbase";
import { pb } from "./pocketbase";
import type { AdminRole, ModuleKey } from "./types";

export const ROLE_MODULES: Record<AdminRole, ModuleKey[]> = {
  // postContent's roles must match POSTING_ROLES in pocketbase/pb_hooks/admin-posting.pb.js.
  superadmin: ["dashboard", "reports", "tickets", "notifications", "listingApprovals", "postContent", "userVerification", "admins"],
  moderator: ["dashboard", "reports", "listingApprovals", "postContent", "userVerification", "notifications"],
  support: ["dashboard", "tickets"],
  analyst: ["dashboard"],
};

export function canAccess(role: AdminRole | null, moduleKey: ModuleKey): boolean {
  if (!role) return false;
  return ROLE_MODULES[role].includes(moduleKey);
}

type AdminProfile = { uid: string; email: string; role: AdminRole; name: string };

type AuthState = {
  loading: boolean;
  admin: AdminProfile | null;
  /** Signed in, but not a recognized admin role — distinct from `loading` so the login page can show a clear message instead of spinning forever. */
  deniedReason: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOutNow: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function toProfile(record: RecordModel | null): AdminProfile | null {
  if (!record) return null;
  const role = record.role as AdminRole | undefined;
  if (!role) return null;
  return { uid: record.id, email: record.email ?? "", role, name: record.name || record.email || "Admin" };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [deniedReason, setDeniedReason] = useState<string | null>(null);

  useEffect(() => {
    // `onChange`'s second arg fires it once immediately with whatever the
    // authStore already has (restored from localStorage on a page
    // refresh), then again on every future sign-in/out.
    const unsub = pb.authStore.onChange((_token, record) => {
      setDeniedReason(null);

      if (!record || pb.authStore.record?.collectionName !== "admins") {
        setAdmin(null);
        if (record) setDeniedReason("This account doesn't have admin console access.");
        setLoading(false);
        return;
      }

      const profile = toProfile(record);
      if (!profile) {
        setAdmin(null);
        setDeniedReason("Admin role not set — ask a superadmin to re-add you.");
      } else {
        setAdmin(profile);
      }
      setLoading(false);
    }, true);

    // The saved session holds the role as it was at sign-in. Re-read it, so
    // a role change (or removal) by a superadmin applies on the next load;
    // a session ended by that change is cleared here.
    if (pb.authStore.isValid && pb.authStore.record?.collectionName === "admins") {
      pb.collection("admins")
        .authRefresh({ requestKey: null })
        .catch((err) => {
          if (err instanceof ClientResponseError && (err.status === 401 || err.status === 403 || err.status === 404)) {
            pb.authStore.clear();
          }
        });
    }

    return unsub;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      admin,
      deniedReason,
      signIn: async (email, password) => {
        try {
          await pb.collection("admins").authWithPassword(email, password);
        } catch (err) {
          if (err instanceof ClientResponseError && err.status === 400) {
            throw new Error("Incorrect email or password.");
          }
          throw err;
        }
      },
      signOutNow: async () => {
        pb.authStore.clear();
      },
    }),
    [loading, admin, deniedReason]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
